import type { PipelineStatus, PhaseStatus } from '../../projects/types';

/**
 * Iconos SVG inline para estados
 */
const StatusIcons = {
  completed: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  running: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="spin">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" strokeDasharray="22 22" />
    </svg>
  ),
  pending: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
    </svg>
  ),
  failed: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M6 6L10 10M10 6L6 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M8 5V9M8 11V11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  queued: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" strokeOpacity="0.5" />
      <circle cx="8" cy="8" r="3" fill="currentColor" fillOpacity="0.5" />
    </svg>
  ),
};

/**
 * Nombres de las fases en español
 */
const PHASE_NAMES: Record<string, string> = {
  bounds: 'Área de Estudio',
  h3Grids: 'Mallas H3',
  baseNetwork: 'Red Base',
  urbanNetwork: 'Red Urbana',
  syntax: 'Sintaxis',
};

/**
 * Colores CSS por estado
 */
const STATUS_COLORS: Record<PhaseStatus['status'], string> = {
  completed: 'var(--color-success, #28a745)',
  running: 'var(--accent-primary, #2fa4b5)',
  pending: 'var(--text-tertiary, #6c757d)',
  queued: 'var(--text-secondary, #8a9ba8)',
  failed: 'var(--color-error, #dc3545)',
  warning: 'var(--color-warning, #ffc107)',
};

/**
 * Formatea duración en milisegundos a formato legible
 */
function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

interface PipelineStatusPanelProps {
  pipeline: PipelineStatus;
  onRetry?: () => void;
  retrying?: boolean;
  onRetrySyntax?: () => void;
  retrySyntaxing?: boolean;
}

/**
 * Panel de visualización del estado del pipeline
 *
 * @remarks
 * Muestra las 5 fases del pipeline con su estado actual,
 * tiempo de ejecución y detalles. Incluye barra de progreso.
 */
export function PipelineStatusPanel({
  pipeline,
  onRetry,
  retrying = false,
  onRetrySyntax,
  retrySyntaxing = false,
}: PipelineStatusPanelProps): JSX.Element {
  const phases = [
    { key: 'bounds', data: pipeline.phases.bounds },
    { key: 'h3Grids', data: pipeline.phases.h3Grids },
    { key: 'baseNetwork', data: pipeline.phases.baseNetwork },
    { key: 'urbanNetwork', data: pipeline.phases.urbanNetwork },
    { key: 'syntax', data: pipeline.phases.syntax },
  ];

  // Red urbana lista, sintaxis todavía calculando
  const isNetworkReadySyntaxPending =
    pipeline.overallStatus === 'building' &&
    pipeline.phases.urbanNetwork.status === 'completed' &&
    (pipeline.phases.syntax.status === 'running' || pipeline.phases.syntax.status === 'queued');

  // Sintaxis falló pero red está operativa
  const isSyntaxOnlyFailure = pipeline.overallStatus === 'warning';

  const showPipelineRetryButton = pipeline.overallStatus === 'error';
  const showSyntaxRetryButton = isSyntaxOnlyFailure;

  return (
    <div className="pipeline-status-panel">
      {/* Header con progreso */}
      <div className="pipeline-header">
        <div className="pipeline-title">
          <span className="pipeline-label">Pipeline</span>
          <span className={`pipeline-overall-status status-${pipeline.overallStatus}`}>
            {pipeline.overallStatus === 'ready' && '● Listo'}
            {pipeline.overallStatus === 'building' && (
              isNetworkReadySyntaxPending
                ? '◑ Red lista — Sintaxis en cálculo'
                : '◐ Construyendo...'
            )}
            {pipeline.overallStatus === 'pending' && '○ Pendiente'}
            {pipeline.overallStatus === 'error' && '✕ Error'}
            {pipeline.overallStatus === 'warning' && '⚠ Red operativa — Sintaxis falló'}
          </span>
        </div>
        <div className="pipeline-progress-container">
          <div className="pipeline-progress-bar">
            <div
              className={`pipeline-progress-fill status-${pipeline.overallStatus}`}
              style={{ width: `${pipeline.progressPercent}%` }}
            />
          </div>
          <span className="pipeline-progress-text">{pipeline.progressPercent}%</span>
        </div>
      </div>

      {/* Fases */}
      <div className="pipeline-phases">
        {phases.map(({ key, data }, index) => (
          <div key={key} className={`pipeline-phase phase-${data.status}`}>
            <div className="phase-connector">
              {index > 0 && <div className={`connector-line status-${phases[index - 1].data.status}`} />}
              <div className="phase-icon" style={{ color: STATUS_COLORS[data.status] }}>
                {StatusIcons[data.status]}
              </div>
              {index < phases.length - 1 && <div className={`connector-line status-${data.status}`} />}
            </div>
            <div className="phase-content">
              <div className="phase-name">{PHASE_NAMES[key]}</div>
              <div className="phase-details">
                {data.details && <span className="phase-detail-text">{data.details}</span>}
                {data.durationMs !== null && (
                  <span className="phase-duration">{formatDuration(data.durationMs)}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Error si existe */}
      {pipeline.lastError && (
        <div className="pipeline-error">
          <div className="error-header">
            <span className="error-phase">Error en {PHASE_NAMES[pipeline.lastError.phase] || pipeline.lastError.phase}</span>
            <span className="error-time">
              {new Date(pipeline.lastError.occurredAt).toLocaleString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          <div className="error-message">{pipeline.lastError.message}</div>
        </div>
      )}

      {/* Botones de acción */}
      {(showPipelineRetryButton || showSyntaxRetryButton) && (
        <div className="pipeline-actions">
          {showSyntaxRetryButton && onRetrySyntax && (
            <button
              type="button"
              className="btn-retry"
              onClick={onRetrySyntax}
              disabled={retrySyntaxing}
            >
              {retrySyntaxing ? 'Encolando...' : 'Reintentar Sintaxis'}
            </button>
          )}
          {showPipelineRetryButton && onRetry && (
            <button
              type="button"
              className="btn-retry btn-retry-secondary"
              onClick={onRetry}
              disabled={retrying}
            >
              {retrying ? 'Reintentando...' : 'Reintentar Pipeline'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
